import React from "react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { TokenIcon } from "@/components/TokenIcon";
import { Sparkline } from "@/components/ui/Sparkline";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import {
  AlertTagBadges,
  BookmarkButton,
  ExchangeStack,
  VerifiedMark,
} from "@/components/alerts/AlertBits";
import {
  changeTextClass,
  formatAlertPrice,
  formatChange,
  formatTimeAgo,
  useAlertCardData,
} from "@/hooks/useAlertCardData";
import { exchangeBrand, marketTypeLabel } from "@/lib/exchange-brand";

/** Column widths shared by the header and every row (Figma desktop table). */
export const ALERT_COLUMNS = {
  asset: "min-w-[150px] flex-[1.4]",
  listing: "min-w-[120px] flex-[1.3]",
  price: "min-w-[96px] flex-1",
  change: "w-[80px] shrink-0",
  exchanges: "w-[68px] shrink-0",
  actions: "w-[168px] shrink-0",
};

export const CryptoAlertTableHeader: React.FC = () => (
  <div className="flex items-start gap-3 px-3 text-xs leading-[18px] text-[#a3a3a3]">
    <span className={ALERT_COLUMNS.asset}>Asset</span>
    <span className={ALERT_COLUMNS.listing}>Listing Info</span>
    <span className={ALERT_COLUMNS.price}>Price Info</span>
    <span className={`${ALERT_COLUMNS.change} text-center`}>Change</span>
    <span className={ALERT_COLUMNS.exchanges}>Exchanges</span>
    <span className={`${ALERT_COLUMNS.actions} text-right`}>Actions</span>
  </div>
);

interface CryptoAlertRowProps {
  alert: CryptoAlertData;
  onViewPositions?: (alert: CryptoAlertData) => void;
  onDetailsClick?: () => void;
  onTrade?: (alert: CryptoAlertData) => void;
}

/** One alert as a desktop table row (redesign). */
export const CryptoAlertRow: React.FC<CryptoAlertRowProps> = ({
  alert,
  onViewPositions,
  onDetailsClick,
  onTrade,
}) => {
  const isListing = alert.type === "new_coin";
  const d = useAlertCardData(alert);
  const source = exchangeBrand(alert.exchange);
  const canTrade = !d.allPriceDataUnavailable;

  const priceLabel = d.bestPosition
    ? d.bestPosition.status === "OPEN"
      ? `${d.positionLabel} open at`
      : `${d.positionLabel} closed at`
    : "Alert Price";
  const secondaryPrice = d.bestPosition
    ? d.bestPosition.status === "CLOSED"
      ? d.bestPosition.exitPrice
      : d.bestPosition.entryPrice
    : alert.price;
  const change =
    d.bestPosition?.status === "CLOSED" ? d.bestPosition.pnlPercentage : d.priceChange;

  return (
    <div ref={d.ref} className="glass flex items-center gap-3 p-3">
      {/* Asset */}
      <div className={`flex items-center gap-2 ${ALERT_COLUMNS.asset}`}>
        <TokenIcon
          symbol={alert.currency}
          logo={alert.tokenMetadata?.logo}
          size="lg"
          className="!h-14 !w-14"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-1">
            <p className="truncate text-sm font-semibold leading-5 text-white">{alert.currency}</p>
            {alert.tokenMetadata && <VerifiedMark />}
          </div>
          {alert.tokenMetadata?.name && (
            <p className="truncate text-xs leading-[18px] text-[#a3a3a3]">
              {alert.tokenMetadata.name}
            </p>
          )}
          <AlertTagBadges isNew={d.isNew} isListing={isListing} tags={[]} />
          {alert.hasAutotraded && (
            <span className="text-[10px] leading-3 text-[#56d799]">Autotraded 🤖</span>
          )}
        </div>
      </div>

      {/* Listing info */}
      <div className={`flex items-center gap-2 ${ALERT_COLUMNS.listing}`}>
        <ExchangeLogo code={alert.exchange} size={28} />
        <div className="min-w-0">
          <p className="truncate text-xs leading-[18px] text-[#d4d4d4]">
            {source.name} {marketTypeLabel(alert.exchangeType)}
          </p>
          <p className="text-[8px] leading-[10px] text-[#a3a3a3]">
            {formatTimeAgo(alert.createdAt, true)}
          </p>
        </div>
      </div>

      {/* Price info */}
      <div className={`flex flex-col gap-0.5 whitespace-nowrap ${ALERT_COLUMNS.price}`}>
        {d.allPriceDataUnavailable ? (
          <p className="text-xs text-[#a3a3a3]">Not available for trading yet</p>
        ) : (
          <>
            <div className="flex items-center gap-1">
              {d.isPriceLoading ? (
                <span className="h-5 w-20 animate-pulse rounded bg-gray-600/30" />
              ) : (
                <p className="text-sm font-semibold leading-5 text-white">
                  {formatAlertPrice(d.livePrice)}
                </p>
              )}
              {d.isSubscribed && d.livePrice && (
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" aria-label="Live" />
              )}
            </div>
            <p className="text-xs leading-[18px] text-[#a3a3a3]">
              {formatAlertPrice(secondaryPrice)}
            </p>
            <p className="text-[8px] leading-[10px] text-[#a3a3a3]">{priceLabel}</p>
          </>
        )}
      </div>

      {/* Change */}
      <div className={`flex flex-col items-center gap-0.5 ${ALERT_COLUMNS.change}`}>
        {change == null ? (
          d.sparkline.length > 1 ? null : <p className="text-sm text-[#737373]">–</p>
        ) : (
          <p className={`text-sm font-semibold leading-5 ${changeTextClass(change)}`}>
            {d.bestPosition?.status === "CLOSED"
              ? `PnL: ${change.toFixed(2)}%`
              : formatChange(change)}
          </p>
        )}
        {d.sparkline.length > 1 && <Sparkline values={d.sparkline} width={72} height={30} />}
      </div>

      {/* Exchanges tradable at alert time */}
      <div
        className={ALERT_COLUMNS.exchanges}
        title={
          d.tradableExchanges.length
            ? `Tradable on ${d.tradableExchanges.map((c) => exchangeBrand(c).name).join(", ")}`
            : "Not tradable on any exchange at alert time"
        }
      >
        {d.tradableExchanges.length ? (
          <ExchangeStack codes={d.tradableExchanges} />
        ) : (
          <span className="text-xs text-[#737373]">–</span>
        )}
      </div>

      {/* Actions */}
      <div className={`flex items-center justify-end gap-3 ${ALERT_COLUMNS.actions}`}>
        <div className="flex flex-col items-stretch gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDetailsClick?.();
            }}
            className="min-w-[96px] rounded-full border border-[#404040] bg-[#171717] px-3 py-1 text-xs font-medium leading-[18px] text-white hover:bg-[#262626]"
          >
            Details
          </button>
          {canTrade && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (d.positions.length > 0 && onViewPositions) onViewPositions(alert);
                else if (d.positions.length === 0 && onTrade) onTrade(alert);
              }}
              aria-label={
                d.positions.length === 0
                  ? "Trade"
                  : d.positions.length > 1
                    ? `View positions (${d.positions.length})`
                    : "View positions"
              }
              className="min-w-[96px] whitespace-nowrap rounded-full border-y border-r border-[#8ee7b7] bg-accent px-3 py-1 text-xs font-medium leading-[18px] text-white hover:bg-accent-hover"
            >
              {d.positions.length === 0
                ? "Trade"
                : d.positions.length > 1
                  ? `Positions (${d.positions.length})`
                  : "Positions"}
            </button>
          )}
        </div>
        <BookmarkButton alertId={alert.id} />
      </div>
    </div>
  );
};
